<template>
  <div class="frame-wrapper landmark-list">
    <!-- メッセージ -->
    <div class="sticky-message" v-if="stickyMessage">
      {{ stickyMessage }}
    </div>
    <!-- Floating Action Button -->
    <div class="tool-buttons" v-if="isShowMode">
      <div class="add-landmark" @click="changeToAddMode"></div>
    </div>
    <!-- マーカー移動時のボタン -->
    <div v-if="markerDragged" class="centered-box-on-map save-marker-buttons">
      <div class="centered-item">
        <div class="button-area">
          <span v-if="isAddMode" class="btn btn-submit" @click="openForm">
            入力する
          </span>
          <span v-else class="btn btn-submit" @click="updatePosition">
            位置を保存
          </span>
          <span class="btn" @click="undoDraggingMarker">キャンセル</span>
        </div>
      </div>
    </div>
    <!-- マーカー詳細編集 -->
    <div v-if="showForm" class="center-frame-container">
      <transition name="scale" @after-leave="hideForm" appear>
        <div class="center-frame">
          <div class="title-area">
            <div></div>
            <div class="btn-close" @click="hideForm"></div>
          </div>

          <validation-observer ref="obs" v-slot="ObserverProps">
            <section class="section">
              <div class="container">
                <dl class="columns">
                  <dt>名前</dt>
                  <dd>
                    <ValidationProvider rules="max:100" v-slot="ProviderProps">
                      <input v-model="form.name" type="text" />
                      <div class="validation-error">
                        {{ ProviderProps.errors[0] }}
                      </div>
                    </ValidationProvider>
                  </dd>
                  <dt>位置</dt>
                  <dd>
                    <span>
                      {{ selectedMarker.getPosition().lat() }},
                      {{ selectedMarker.getPosition().lng() }}
                    </span>
                  </dd>
                  <dt>アイコン</dt>
                  <dd>
                    <div
                      v-for="iconSet in iconSets"
                      :key="iconSet.name"
                      class="image-radio-button-group"
                    >
                      <div class="group-name">{{ iconSet.name }}</div>
                      <div
                        v-for="icon in iconSet.icons"
                        :key="icon.image"
                        class="image-radio-button"
                        :style="{
                          background: icon.image === form.icon ? '#ddd' : '',
                        }"
                        v-tooltip="{ content: icon.name, delay: { show: 0 } }"
                      >
                        <label>
                          <input
                            type="radio"
                            name="icon_image"
                            :value="icon.image"
                            v-model="form.icon"
                          />
                          <img v-bind:src="buildMarkerIcon(icon.image)" />
                        </label>
                      </div>
                    </div>
                  </dd>
                </dl>
              </div>
            </section>

            <div class="button-area">
              <div class="left-box" />
              <div class="center-box">
                <span
                  class="btn btn-submit"
                  :class="{ disable: ObserverProps.invalid }"
                  @click="onSubmit"
                >
                  保存</span
                >
                <span class="btn btn-cancel" @click="hideForm">
                  キャンセル</span
                >
              </div>
              <div class="right-box">
                <span
                  class="text"
                  :class="{ active: anyInput && !isAddMode }"
                  @click="onDelete"
                >
                  削除
                </span>
              </div>
            </div>
          </validation-observer>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import GoogleMaps from "@/mixins/googlemaps.js";
import Loader from "@/mixins/loader";
import Dialog from "@/mixins/dialog";
import Landmark from "@/mixins/landmark";

// 画面モード
const MODE = {
  SHOW: 1, // 参照モード（初期状態）
  ADD: 2, // 追加モード
  MOVE: 3, // マーカー移動モード
  EDIT: 4, // 編集モード
};

// ランドマーク
const DEFAULT_ICON = { image: "ic_landmark.png", name: "無地" };

export default {
  mixins: [GoogleMaps, Loader, Dialog, Landmark],

  data() {
    return {
      // 現在の画面モード
      mode: MODE.SHOW,
      // 画面上部のメッセージ
      stickyMessage: "",
      // ランドマークのマーカーの実体(google.maps.Marker配列)
      markers: [],
      // 選択中・移動中のマーカー
      selectedMarker: null,
      // マーカーをドラッグ中ならtrue
      markerDragged: false,
      listenerNames: {
        clickMap: null,
        dragend: null,
      },
      // マーカー詳細フォームを表示するならtrue、消去するならfalse
      showForm: false,
      // マーカー詳細フォームのバインド変数
      form: {
        name: null,
        icon: null,
      },
      // ランドマークとして表示可能な画像リスト
      iconSets: [
        {
          name: "シンプル",
          icons: [
            DEFAULT_ICON,
            { image: "ic_landmark001.png", name: "ノーマル" },
            { image: "ic_landmark002.png", name: "1" },
            { image: "ic_landmark003.png", name: "2" },
            { image: "ic_landmark004.png", name: "3" },
            { image: "ic_landmark005.png", name: "4" },
            { image: "ic_landmark006.png", name: "5" },
            { image: "ic_landmark007.png", name: "6" },
            { image: "ic_landmark008.png", name: "7" },
            { image: "ic_landmark009.png", name: "8" },
            { image: "ic_landmark010.png", name: "9" },
          ],
        },
        {
          name: "建物",
          icons: [
            { image: "ic_landmark201.png", name: "バー" },
            { image: "ic_landmark202.png", name: "レストラン" },
            { image: "ic_landmark203.png", name: "ファストフード" },
            { image: "ic_landmark204.png", name: "建物" },
            { image: "ic_landmark205.png", name: "コンビニ" },
            { image: "ic_landmark206.png", name: "家-1" },
            { image: "ic_landmark207.png", name: "家-2" },
            { image: "ic_landmark208.png", name: "家-3" },
            { image: "ic_landmark209.png", name: "工場" },
            { image: "ic_landmark210.png", name: "ビル-1" },
            { image: "ic_landmark211.png", name: "ビル-2" },
          ],
        },
        {
          name: "交通",
          icons: [
            { image: "ic_landmark301.png", name: "車" },
            { image: "ic_landmark302.png", name: "バス" },
            { image: "ic_landmark303.png", name: "トラック" },
            { image: "ic_landmark304.png", name: "自転車" },
            { image: "ic_landmark305.png", name: "トラクター" },
          ],
        },
        {
          name: "地図記号",
          icons: [
            { image: "ic_landmark401.png", name: "消防署" },
            { image: "ic_landmark402.png", name: "郵便局" },
            { image: "ic_landmark403.png", name: "学校" },
            { image: "ic_landmark404.png", name: "病院" },
            { image: "ic_landmark405.png", name: "神社" },
            { image: "ic_landmark406.png", name: "寺院" },
            { image: "ic_landmark407.png", name: "博物館" },
            { image: "ic_landmark408.png", name: "工場" },
            { image: "ic_landmark409.png", name: "発電所" },
            { image: "ic_landmark410.png", name: "警察署" },
            { image: "ic_landmark411.png", name: "保健所" },
            { image: "ic_landmark412.png", name: "漁港" },
            { image: "ic_landmark413.png", name: "教会" },
            { image: "ic_landmark414.png", name: "温泉" },
            { image: "ic_landmark415.png", name: "史跡・名勝・天然記念物" },
          ],
        },
        {
          name: "その他",
          icons: [
            { image: "ic_landmark101.png", name: "星" },
            { image: "ic_landmark102.png", name: "スパナ" },
            { image: "ic_landmark103.png", name: "人" },
            { image: "ic_landmark104.png", name: "学生" },
            { image: "ic_landmark105.png", name: "木" },
            { image: "ic_landmark106.png", name: "受話器" },
            { image: "ic_landmark107.png", name: "ギア" },
            { image: "ic_landmark108.png", name: "道標" },
            { image: "ic_landmark109.png", name: "パーキング" },
            { image: "ic_landmark110.png", name: "たばこ" },
            { image: "ic_landmark111.png", name: "水" },
            { image: "ic_landmark112.png", name: "注意" },
            { image: "ic_landmark113.png", name: "ベル" },
          ],
        },
      ],
    };
  },

  computed: {
    anyInput() {
      return Object.keys(this.form).some((key) => {
        if (this.form[key]) {
          return true;
        }
      });
    },

    isShowMode() {
      return this.mode === MODE.SHOW;
    },

    isAddMode() {
      return this.mode === MODE.ADD;
    },

    isMoveMode() {
      return this.mode === MODE.MOVE;
    },
  },

  async mounted() {
    this.$emit("showMap");

    await this.loadMarkers();
  },

  destroyed() {
    // カーソルをデフォルトに戻しておく
    this.map.setOptions({ draggableCursor: undefined });

    // リスナー削除
    if (this.listenerNames.clickMap) {
      this.google.maps.event.removeListener(this.listenerNames.clickMap);
    }
    if (this.listenerNames.dragend) {
      this.google.maps.event.removeListener(this.listenerNames.dragend);
    }

    // ドラッグ中マーカーをもとに戻す
    if (this.markerDragged) {
      this.undoDraggingMarker();
    }

    // 画面上のランドマークマーカーの操作禁止
    this.denyActionToMarkersExceptForDraggingOf(null);
    // clickableのみ再設定
    this.markers.forEach((marker) => {
      marker.setClickable(true);
    });
  },

  methods: {
    ...mapActions([
      "retrieveLandmarks",
      "insertLandmark",
      "updateLandmark",
      "deleteLandmark",
    ]),
    ...mapGetters(["getLandmarkMarkers"]),

    async loadMarkers() {
      // 選択状態をクリア
      this.initializeStates();
      // 最新のランドマーク情報を取得
      await this.retrieveLandmarks();
      // 現在マップに残っているマーカーをクリア
      this.markers = this.getLandmarkMarkers();
      this.clearMarkers();
      // マーカー表示
      this.putLandmarkMarkers((marker) => {
        // ドラッグ許可
        marker.setDraggable(true);

        // ドラッグ終了イベントリスナ
        this.listenerNames.dragend = marker.addListener("dragend", () => {
          this.startDraggingMarker(marker);

          if (this.mode !== MODE.MOVE) {
            this.mode = MODE.MOVE;
            this.stickyMessage =
              "ドラッグでマーカーの位置を決めたら[位置を保存]をクリックして下さい。";
          }
        });

        this.markers.push(marker);
      });
      // マーカークリックイベントリスナー
      this.setClickListenerToMarkers();
    },

    initializeStates() {
      this.showForm = false;

      // フォーム消去の描画に影響を与えるようなのでフォーム消去の描画後に各種フラグを初期化する
      this.$nextTick(() => {
        this.stickyMessage = "";
        this.selectedMarker = null;
        this.markerDragged = false;
        this.mode = MODE.SHOW;
      });
    },

    setClickListenerToMarkers() {
      // ランドマークマーカーにクリックイベントリスナーを追加
      this.markers.forEach((marker) => {
        marker.addListener("click", () => {
          this.selectedMarker = marker;
          // フォーム表示
          this.openForm(marker);
          // 編集モード
          this.mode = MODE.EDIT;
        });
        marker.setClickable(true);
      });
    },

    unsetClickListenerToMarkers() {
      // ランドマークマーカーのクリックイベントリスナー削除
      this.markers.forEach((marker) => {
        this.google.maps.event.clearListeners(marker, "click");
        marker.setClickable(false);
      });
    },

    clearMarkers() {
      this.markers.forEach((marker) => {
        this.releaseMarker(marker);
      });
      // マーカーの実体を全て削除
      this.markers.splice(0, this.markers.length);
    },

    releaseMarker(marker) {
      // マーカーとMapとの紐付けを解除しイベントリスナーを削除
      marker.setMap(null);
      this.google.maps.event.clearInstanceListeners(marker);
    },

    startDraggingMarker(marker) {
      if (!this.markerDragged) {
        this.selectedMarker = marker;
        this.markerDragged = true;

        // 引数のマーカーのドラッグ以外は操作禁止
        this.denyActionToMarkersExceptForDraggingOf(marker);
      }
    },

    undoDraggingMarker() {
      if (this.selectedMarker) {
        if (this.mode === MODE.ADD) {
          // マーカーを消す
          this.releaseMarker(this.selectedMarker);
        } else {
          // マーカーの位置をもとに戻す
          const landmark = this.selectedMarker.get("landmark");
          this.selectedMarker.setPosition(
            new this.google.maps.LatLng(landmark.latitude, landmark.longitude)
          );
        }
      }

      // 選択状態をクリア
      this.initializeStates();

      // 全マーカーをクリック・ドラッグ可能に戻す
      this.setClickListenerToMarkers();
      this.markers.forEach((marker) => {
        marker.setDraggable(true);
      });
    },

    denyActionToMarkersExceptForDraggingOf(marker) {
      // クリック禁止
      this.unsetClickListenerToMarkers();
      this.markers.forEach((m) => {
        // 引数marker以外はドラッグ禁止
        if (marker !== m) {
          m.setDraggable(false);
        }
      });
    },

    async updatePosition() {
      await this.loading(async () => {
        try {
          // 緯度経度を上書き
          const param = this.selectedMarker.get("landmark");
          param.latitude = this.selectedMarker.getPosition().lat();
          param.longitude = this.selectedMarker.getPosition().lng();

          await this.updateLandmark(param);

          // 成功通知
          this.$toasted.success("位置を保存しました。", {
            position: "top-center",
            duration: 3000,
          });

          await this.loadMarkers();
        } catch (error) {
          alert("通信エラー");
          console.log(error);
          throw error;
        }
      });
    },

    openForm() {
      if (this.selectedMarker) {
        // フォームの初期値を設定
        if (this.mode === MODE.ADD) {
          this.form.name = `ランドマーク${this.markers.length + 1}`;
          this.form.icon = DEFAULT_ICON['image'];
        } else {
          const landmark = this.selectedMarker.get("landmark");
          this.form.name = landmark.name;
          this.form.icon = landmark.icon;
        }

        this.showForm = true;
      }
    },

    hideForm() {
      // フォームデータ初期化
      Object.keys(this.form).forEach((key) => {
        this.form[key] = null;
      });

      if (this.mode === MODE.ADD) {
        // 追加モードの場合はフォームを消すのみ
        this.showForm = false;
      } else {
        this.initializeStates();
      }
    },

    async onSubmit() {
      this.loading(async () => {
        try {
          let reqParams;
          if (this.mode === MODE.ADD) {
            // フォームでは変更不可のデータをパラメータに用意
            reqParams = {
              latitude: this.selectedMarker.getPosition().lat(),
              longitude: this.selectedMarker.getPosition().lng(),
            };
          } else {
            // 元のデータ
            reqParams = this.selectedMarker.get("landmark");
          }
          // フォームデータを上書き
          Object.assign(reqParams, this.form);

          if (this.mode === MODE.ADD) {
            // 登録
            await this.insertLandmark(reqParams);
          } else {
            // 更新
            await this.updateLandmark(reqParams);
          }

          // 編集画面消去
          this.hideForm();

          // 成功通知
          const message =
            this.mode === MODE.ADD ? "登録しました。" : "変更を保存しました。";
          this.$toasted.success(message, {
            position: "top-center",
            duration: 3000,
          });

          // 新規追加用マーカーを開放
          if (this.mode === MODE.ADD) {
            this.releaseMarker(this.selectedMarker);
          }
          // マーカーをリロード
          await this.loadMarkers();
        } catch (error) {
          alert("通信エラー");
          console.log(error);
          throw error;
        }
      });
    },

    async onDelete() {
      this.showConfirm("削除しますか？", async () => {
        this.loading(async () => {
          try {
            // 削除
            const params = this.selectedMarker.get("landmark");
            await this.deleteLandmark(params);

            // 編集画面消去
            this.hideForm();

            // 成功通知
            this.$toasted.success("マーカーを削除しました。", {
              position: "top-center",
              duration: 3000,
            });

            // マーカーをリロード
            await this.loadMarkers();
          } catch (error) {
            alert("通信エラー");
            console.log(error);
            throw error;
          }
        });
      });
    },

    async changeToAddMode() {
      // 地図クリックでマーカーを設置できるようにする
      this.map.setOptions({ draggableCursor: "crosshair" });
      this.listenerNames.clickMap = this.map.addListener(
        "click",
        this.addMarker
      );

      // 画面上のマーカーの操作禁止
      this.denyActionToMarkersExceptForDraggingOf(null);

      this.stickyMessage = "マーカーを追加したい位置でクリックして下さい。";

      this.mode = MODE.ADD;
    },

    async addMarker(e) {
      // マーカーを描画
      const marker = this.putNewMarker(e.latLng);

      // マーカーを追加したらそれ以上追加できないようにする
      if (this.listenerNames.clickMap) {
        this.google.maps.event.removeListener(this.listenerNames.clickMap);
      }

      // カーソルを戻す
      this.map.setOptions({ draggableCursor: undefined });

      // ドラッグ開始
      this.startDraggingMarker(marker);

      this.stickyMessage =
        "ドラッグでマーカーの位置を決めたら[入力する]をクリックして下さい。";
    },

    putNewMarker(latLng) {
      // マーカー追加
      const { Marker } = this.google.maps;
      const marker = new Marker({
        position: latLng,
        map: this.map,
        icon: this.buildMarkerIconProps(DEFAULT_ICON['image']),
        draggable: true,
      });

      return marker;
    },
  },
};
</script>

<style lang="scss">
.landmark-list {
  &.frame-wrapper {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
  }

  .tool-buttons {
    display: flex;
    position: absolute;

    .add-landmark {
      display: block;
      width: 40px;
      height: 40px;
      border-radius: 50%;
      background: white url("~@/assets/icons/ic_add_active.svg") no-repeat
        center center;
      margin: 15px;
      cursor: pointer;
    }
  }

  .sticky-message {
    position: absolute;
    top: 0;
    width: 100%;
    background-color: var(--purple);
    color: white;
    text-align: center;
  }

  .btn {
    display: block;
    background: white;
    font-size: 14px;
    text-align: center;
    width: 130px;
    height: 30px;
    border-radius: 15px;
    color: var(--purple);
    padding: 6px 20px;
    margin: 10px auto 10px;
    cursor: pointer;
    &.disable {
      opacity: 0.5;
      cursor: inherit;
      pointer-events: none;
    }
    &:nth-of-type(n + 2) {
      margin-left: 1rem;
    }
    &.btn-submit {
      background: var(--purple);
      color: #fff;
    }
  }

  .centered-box-on-map {
    position: fixed;
    left: calc(50% + (250px / 2));
    z-index: 1;

    .centered-item {
      position: relative;
      left: -50%;
    }

    &.save-marker-buttons {
      bottom: 0;
      margin-bottom: 1rem;
    }

    .button-area {
      display: flex;
      justify-content: flex-start;
      width: 100%;

      .btn {
        width: 170px;
      }
    }
  }

  .center-frame-container {
    position: absolute;
    //   display: none;
    display: flex;
    justify-content: center;
    align-items: center;
    margin: 0;
    padding: 0;
    width: 100%;
    height: 100vh;
    box-sizing: border-box;
    background: rgba(0, 0, 0, 0.5);
    z-index: 90;

    .center-frame {
      margin: 0;
      padding: 15px 30px;
      width: 80%;
      max-width: 800px;
      //   height: 80vh;
      max-height: 80vh;
      overflow-y: scroll;
      position: absolute;
      top: 10vh;
      background: white;
      border-radius: 20px;
      box-shadow: 0 0 30px 15px rgba(0, 0, 0, 0.3);
      // animation: scaleAnimIn 0.5s;
    }

    //   &.active {
    //     display: flex;
    //     /* animation: scaleAnimIn 0.3s ease-out; */
    //   }

    .title-area {
      width: 100%;
      height: 80px;
      padding: 0;
      box-sizing: border-box;
      display: grid;
      grid-template-columns: 1fr 32px;
      align-items: center;
      border-bottom: solid 1px #ddd;
    }

    .btn-close {
      cursor: pointer;
      width: 32px;
      height: 32px;
      background: url("~@/assets/icons/ic_cancel.svg") no-repeat center center;
      background-size: contain;
    }

    .button-area {
      display: flex;
      justify-content: flex-start;
      width: 100%;

      .right-box {
        right: 0;
        bottom: 0;
        width: 70px;
      }
      .center-box {
        display: inline;
        text-align: center;
        width: calc(100% - 140px);
      }
      .left-box {
        left: 0;
        bottom: 0;
        width: 70px;
      }

      .btn {
        display: inline-block;
        background: white;
        font-size: 14px;
        text-align: center;
        width: 130px;
        height: 30px;
        border-radius: 15px;
        color: var(--purple);
        padding: 6px 20px;
        margin: 10px auto 10px;
        cursor: pointer;
        &.disable {
          opacity: 0.5;
          cursor: inherit;
        }
        &.btn-submit {
          background: var(--purple);
          color: #fff;
          margin-left: 3px;
          margin-right: 3px;
        }
        &.btn-cancel {
          background: var(--purple-tertiary);
          color: #000;
          margin-left: 3px;
          margin-right: 3px;
        }
      }

      .text {
        right: 0;
        bottom: 0;
        display: block;
        font-size: 14px;
        padding: 3px 0px;
        margin: 10px 0;
        text-align: center;
        color: var(--purple);
        border-bottom: solid 4px transparent;
        opacity: 0.5;
        pointer-events: none;
      }
      .text.active {
        border-bottom: solid 4px var(--purple-secondary);
        opacity: 1;
        cursor: pointer;
        pointer-events: inherit;
      }
      .text::before {
        white-space: pre;
        content: "    ";
        background: url("~@/assets/icons/ic_delete.svg") no-repeat center left;
        background-size: 20px 20px;
        padding: 0 0 0 5px;
      }
    }

    .section {
      overflow: auto;
      width: 100%;
      height: calc(100% - 130px);
      position: relative;

      .container {
        margin: 0;

        dl.columns {
          width: 100%;
          display: grid;
          grid-template-columns: 170px 1fr;
          align-items: top;
          grid-gap: 0px 0px;
          margin: 0;
          padding: 0;
          font-size: 16px;
          color: #333;
        }

        .columns dt {
          display: block;
          text-align: right;
          width: 100%;
          margin: 0;
          padding: 16px;
        }

        .columns dd {
          display: block;
          width: 100%;
          border-left: solid 1px #ddd;
          margin: 0;
          padding: 16px;

          .input-with-unit {
            display: flex;
            align-items: center;
            flex-wrap: wrap;
            .unit {
              margin-left: 1rem;
              word-break: keep-all;
            }
            input {
              width: auto;
              flex-grow: 2;
            }
          }

          span.textarea {
            white-space: pre-line;
          }

          .image-radio-button-group {
            display: flex;
            flex-flow: wrap;
            .group-name {
              width: 100%;
            }
            &:nth-child(n + 2) {
              .group-name {
                margin-top: 0.5rem;
              }
            }
            .image-radio-button {
              text-align: center;
              border: 1px solid #ccc;
              border-radius: 5px;
              padding: 10px;
              margin: 2px;
            }
            .image-radio-button:hover {
              background: #eee;
            }
            .image-radio-button img {
              height: 42px;
              width: 42px;
            }
            .image-radio-button label {
              cursor: pointer;
            }
          }
        }
        // .columns dt:nth-of-type(odd) {
        //   background: #f3f3f3;
        // }
        // .columns dd:nth-of-type(odd) {
        //   background: #f3f3f3;
        // }

        /* フォーム */

        input[type="text"],
        input[type="date"],
        input[type="number"] {
          width: 100%;
          height: 40px;
          border-radius: 20px;
          border: solid 1px var(--purple);
          padding: 0 16px;
          font-size: 16px;
          outline: none;
        }

        input[type="text"]:focus,
        input[type="date"]:focus,
        input[type="number"]:focus {
          background: var(--purple-tertiary);
        }

        select {
          width: 100%;
          height: 40px;
          border-radius: 20px;
          border: solid 1px var(--purple);
          padding: 0 38px 0 16px;
          font-size: 16px;
          outline: none;
          -webkit-appearance: none;
          -moz-appearance: none;
          appearance: none;
          background: url("~@/assets/icons/ic_dropdown.svg") no-repeat
            calc(100% - 10px) center;
          background-size: 25px 25px;
        }

        select:focus {
          background: url("~@/assets/icons/ic_dropdown.svg") no-repeat
            calc(100% - 10px) center var(--purple-tertiary);
          background-size: 25px 25px;
        }

        textarea {
          font-size: 16px;
          width: 100%;
          min-height: 200px;
          white-space: pre-wrap;
          outline: none;
          -webkit-appearance: none;
          -moz-appearance: none;
          appearance: none;
          border: solid 1px var(--purple);
          padding: 10px;
          border-radius: 10px 10px 0 10px;
          font-family: inherit;
        }

        textarea:focus {
          background: var(--purple-tertiary);
        }

        input[type="radio"] {
          display: none;
        }
        input[type="radio"] + label {
          padding: 0 20px 0 0;
        }
        input[type="radio"]:disabled + label {
          color: #aaa;
        }

        input[type="radio"] + label:before {
          display: inline-block;
          white-space: pre;
          content: "    ";
          background: url("~@/assets/icons/ic_radio_off.svg") no-repeat center
            left;
          background-size: 24px 24px;
          padding: 0 16px 0 0;
        }
        input[type="radio"]:checked + label:before {
          white-space: pre;
          content: "    ";
          background: url("~@/assets/icons/ic_radio_on.svg") no-repeat center
            left;
          background-size: 24px 24px;
          display: inline-block;
        }
        input[type="radio"]:disabled + label:before {
          white-space: pre;
          content: "    ";
          background: url("~@/assets/icons/ic_radio_disabled.svg") no-repeat
            center left;
          background-size: 24px 24px;
          display: inline-block;
        }

        input[type="checkbox"] {
          display: none;
        }
        input[type="checkbox"] + label {
          padding: 0 20px 0 0;
        }

        input[type="checkbox"] + label:before {
          display: inline-block;
          white-space: pre;
          content: "    ";
          background: url("~@/assets/icons/ic_checkbox_off.svg") no-repeat
            center left;
          background-size: 24px 24px;
          padding: 0 16px 0 0;
        }
        input[type="checkbox"]:checked + label:before {
          white-space: pre;
          content: "    ";
          background: url("~@/assets/icons/ic_checkbox_on.svg") no-repeat center
            left;
          background-size: 24px 24px;
          display: inline-block;
        }
      }
    }
  }

  .fade-enter,
  .fade-leave-to {
    opacity: 0;
  }
  .fade-enter-active,
  .fade-leave-active {
    transition: opacity 0.5s;
  }

  .scale-enter,
  .scale-leave-to {
    transform: scale(0.5);
    opacity: 0;
  }
  .scale-enter-active {
    transition: transform 0.25s, opacity 0.25s;
    transition-timing-function: ease-out;
  }
  .scale-leave-active {
    transition: transform 0.25s, opacity 0.25s;
    transition-timing-function: ease-in;
  }
}
</style>
